import { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, MenuItem, Box,
} from '@mui/material';
import { useCreateNode, useUpdateNode } from '../hooks/useNodes';
import type { NodeResponse } from '../api/types';
import { PROTOCOL_LABELS } from '../utils/format';

interface Props {
  open: boolean;
  onClose: () => void;
  editingNode: NodeResponse | null;
}

const PROTOCOLS = Object.keys(PROTOCOL_LABELS);

export function NodeFormDialog({ open, onClose, editingNode }: Props) {
  const createNode = useCreateNode();
  const updateNode = useUpdateNode();
  const isEdit = !!editingNode;

  const [name, setName] = useState('');
  const [protocol, setProtocol] = useState('vmess');
  const [address, setAddress] = useState('');
  const [port, setPort] = useState(443);
  const [region, setRegion] = useState('');
  const [configText, setConfigText] = useState('{"uuid":"","security":"auto","network":"ws"}');

  useEffect(() => {
    if (editingNode) {
      setName(editingNode.name);
      setProtocol(editingNode.protocol);
      setAddress(editingNode.address);
      setPort(editingNode.port);
      setRegion(editingNode.region || '');
      setConfigText(JSON.stringify(editingNode.configJson, null, 2));
    } else {
      setName('');
      setProtocol('vmess');
      setAddress('');
      setPort(443);
      setRegion('');
      setConfigText('{"uuid":"","security":"auto","network":"ws"}');
    }
  }, [editingNode, open]);

  const handleSubmit = async () => {
    let configJson: object;
    try {
      configJson = JSON.parse(configText);
    } catch {
      alert('配置 JSON 格式错误，请检查');
      return;
    }

    if (isEdit && editingNode) {
      await updateNode.mutateAsync({
        id: editingNode.id,
        data: { name, protocol, address, port, region, configJson },
      });
    } else {
      await createNode.mutateAsync({
        name, protocol, address, port, region, configJson,
      });
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? '编辑节点' : '添加节点'}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="节点名称"
            value={name}
            onChange={e => setName(e.target.value)}
            required
            fullWidth
            placeholder="如：东京-01"
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="协议类型"
              value={protocol}
              onChange={e => setProtocol(e.target.value)}
              select
              sx={{ width: 180 }}
            >
              {PROTOCOLS.map(p => (
                <MenuItem key={p} value={p}>{PROTOCOL_LABELS[p] || p}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="端口"
              value={port}
              onChange={e => setPort(Number(e.target.value))}
              type="number"
              required
              sx={{ flex: 1 }}
            />
          </Box>
          <TextField
            label="服务器地址"
            value={address}
            onChange={e => setAddress(e.target.value)}
            required
            fullWidth
            placeholder="jp1.example.com"
          />
          <TextField
            label="地区标签"
            value={region}
            onChange={e => setRegion(e.target.value)}
            fullWidth
            placeholder="日本"
          />
          <TextField
            label="节点配置 (JSON)"
            value={configText}
            onChange={e => setConfigText(e.target.value)}
            multiline
            rows={4}
            fullWidth
            placeholder="VMess/VLESS/SS 等协议配置"
            helperText="JSON 格式，字段需与 v2rayN ProfileItem 兼容"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>取消</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!name || !address}
        >
          {isEdit ? '保存' : '添加'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
